/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import MDBadge from "components/MDBadge";
import { Link } from "react-router-dom";

function DefaultProductCard({
  color,
  icon,
  title,
  description,
  value,
  image,
  imageDark,
  subscribed,
  onClick,
  to,
  productID,
  loading = false,
  pending = false,
  sandbox = false,
  production = false,
  inReview = false,
  rejected = false,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const renderImage = () => (
    <img
      src={darkMode ? image : imageDark}
      height="50em"
      width="50em"
      className="img-fluid"
    />
  );

  const renderIcon = () => (
    <MDBox
      display="grid"
      justifyContent="center"
      alignItems="center"
      bgColor={color}
      color="white"
      width="4rem"
      height="4rem"
      shadow="md"
      borderRadius="lg"
      variant="gradient"
    >
      <Icon fontSize="default">{icon}</Icon>
    </MDBox>
  );

  const renderBadges = () => (
    <MDBox
      display="flex"
      flexDirection="row"
      justifyContent="center"
      width="100%"
      p={2}
      pb={0}
    >
      {production && <MDBadge color="success" badgeContent="Production" />}
      {sandbox && <MDBadge color="info" badgeContent="Sandbox" />}
    </MDBox>
  );

  const renderButton = () => {
    if (rejected) {
      return (
        <MDButton
          component={Link}
          to=""
          variant="outlined"
          size="small"
          color="warning"
        >
          Rejected
        </MDButton>
      );
    }
    if (inReview) {
      return (
        <MDButton
          component={Link}
          to=""
          variant="outlined"
          size="small"
          color="warning"
        >
          In Review
        </MDButton>
      );
    }
    if (pending) {
      return (
        <MDButton
          component={Link}
          to=""
          variant="outlined"
          size="small"
          color="warning"
        >
          Pending
        </MDButton>
      );
    }
    return (
      <MDButton
        component={Link}
        onClick={() => onClick()}
        to={to || ""}
        variant="outlined"
        size="small"
        color={darkMode ? "white" : "success"}
        disabled={loading}
      >
        {subscribed ? "View" : "Request API"}
      </MDButton>
    );
  };

  const renderUpgradeButton = () => {
    if (!production && sandbox) {
      return (
        <MDButton
          component={Link}
          onClick={() => onClick()}
          to={`/get-production-access?key=${productID}`}
          variant="outlined"
          size="small"
          color={darkMode ? "white" : "success"}
          disabled={loading}
          sx={{ marginLeft: "5px" }}
        >
          Upgrade
        </MDButton>
      );
    }
    return null;
  };

  return (
    <Card className={image ? "h-100 align-items-center" : ""}>
      <MDBox
        p={2}
        mx={3}
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
      >
        {image ? renderImage() : renderIcon()}
      </MDBox>
      <MDBox
        pb={2}
        px={2}
        textAlign="center"
        lineHeight={1.5}
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        className="h-100 w-100"
      >
        <MDTypography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
        >
          {title}
        </MDTypography>
        {(production || sandbox) && renderBadges()}
        {description && !value ? null : <Divider />}
        {value && (
          <MDTypography variant="h5" fontWeight="medium">
            {value}
          </MDTypography>
        )}
        <MDBox
          display="flex"
          mt={1.5}
          justifyContent="center"
          className="w-100"
        >
          {renderButton()}
          {renderUpgradeButton()}
        </MDBox>
      </MDBox>
    </Card>
  );
}

DefaultProductCard.propTypes = {
  color: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  imageDark: PropTypes.string,
  subscribed: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  to: PropTypes.string.isRequired,
  productID: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  pending: PropTypes.bool,
  sandbox: PropTypes.bool,
  production: PropTypes.bool,
  inReview: PropTypes.bool,
  rejected: PropTypes.bool,
};

DefaultProductCard.defaultProps = {
  description: "",
  imageDark: "",
  loading: false,
  pending: false,
  sandbox: false,
  production: false,
  inReview: false,
  rejected: false,
};

export default DefaultProductCard;
